/**
 * Financial Calculation Utilities
 *
 * Functions for calculating mortgage payments, cash flow, and investment metrics.
 */

import { FINANCIAL_RATES, LOAN_DEFAULTS } from "@/lib/constants";

export interface MortgageCalculation {
  monthlyPayment: number;
  totalPayment: number;
  totalInterest: number;
  loanAmount: number;
}

export interface CashFlowMetrics {
  monthlyNOI: number;
  annualNOI: number;
  monthlyCashFlow: number;
  annualCashFlow: number;
  capRate: number;
  cashOnCash: number;
}

export interface MonthlyExpenseBreakdown {
  propertyTax: number;
  insurance: number;
  hoa: number;
  management: number;
  vacancy: number;
  maintenance: number;
  other: number;
  totalOperating: number;
  mortgage: number;
  total: number;
}

/**
 * Calculate monthly mortgage payment (P&I)
 * @param principal - Loan amount
 * @param annualRate - Annual interest rate (as percentage, e.g., 6.5)
 * @param termYears - Loan term in years
 * @returns Monthly payment amount
 */
export function calculateMonthlyPayment(
  principal: number,
  annualRate: number = LOAN_DEFAULTS.INTEREST_RATE,
  termYears: number = LOAN_DEFAULTS.TERM_YEARS,
): number {
  if (principal <= 0 || annualRate <= 0 || termYears <= 0) return 0;

  const monthlyRate = annualRate / 100 / 12;
  const numPayments = termYears * 12;

  const payment =
    (principal * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
    (Math.pow(1 + monthlyRate, numPayments) - 1);

  return isNaN(payment) || !isFinite(payment) ? 0 : payment;
}

/**
 * Calculate full mortgage details
 * @param purchasePrice - Property purchase price
 * @param downPaymentPercent - Down payment as percentage (e.g., 25 for 25%)
 * @param annualRate - Annual interest rate as percentage
 * @param termYears - Loan term in years
 * @returns Complete mortgage calculation
 */
export function calculateMortgage(
  purchasePrice: number,
  downPaymentPercent: number = LOAN_DEFAULTS.DOWN_PAYMENT_PERCENT * 100,
  annualRate: number = LOAN_DEFAULTS.INTEREST_RATE,
  termYears: number = LOAN_DEFAULTS.TERM_YEARS,
): MortgageCalculation {
  const downPayment = purchasePrice * (downPaymentPercent / 100);
  const loanAmount = purchasePrice - downPayment;
  const monthlyPayment = calculateMonthlyPayment(
    loanAmount,
    annualRate,
    termYears,
  );
  const totalPayment = monthlyPayment * termYears * 12;
  const totalInterest = totalPayment - loanAmount;

  return {
    monthlyPayment,
    totalPayment,
    totalInterest,
    loanAmount,
  };
}

/**
 * Calculate monthly expense breakdown
 * @param purchasePrice - Property purchase price
 * @param monthlyRent - Monthly rental income
 * @param monthlyMortgage - Monthly mortgage payment (P&I)
 * @param options - Optional expense parameters
 * @returns Detailed expense breakdown
 */
export function calculateMonthlyExpenses(
  purchasePrice: number,
  monthlyRent: number,
  monthlyMortgage: number,
  options: {
    propertyTaxRate?: number; // as percentage
    insuranceRate?: number; // as percentage
    hoaMonthly?: number;
    managementRate?: number; // as percentage
    vacancyRate?: number; // as percentage
    maintenanceRate?: number; // as percentage
    otherMonthly?: number;
  } = {},
): MonthlyExpenseBreakdown {
  const {
    propertyTaxRate = FINANCIAL_RATES.PROPERTY_TAX_RATE * 100,
    insuranceRate = FINANCIAL_RATES.INSURANCE_RATE * 100,
    hoaMonthly = 0,
    managementRate = FINANCIAL_RATES.PROPERTY_MANAGEMENT_RATE * 100,
    vacancyRate = FINANCIAL_RATES.VACANCY_RATE * 100,
    maintenanceRate = FINANCIAL_RATES.MAINTENANCE_RATE * 100,
    otherMonthly = 0,
  } = options;

  const propertyTax = (purchasePrice * (propertyTaxRate / 100)) / 12;
  const insurance = (purchasePrice * (insuranceRate / 100)) / 12;
  const management = monthlyRent * (managementRate / 100);
  const vacancy = monthlyRent * (vacancyRate / 100);
  const maintenance = (purchasePrice * (maintenanceRate / 100)) / 12;

  const totalOperating =
    propertyTax +
    insurance +
    hoaMonthly +
    management +
    vacancy +
    maintenance +
    otherMonthly;
  const total = totalOperating + monthlyMortgage;

  return {
    propertyTax,
    insurance,
    hoa: hoaMonthly,
    management,
    vacancy,
    maintenance,
    other: otherMonthly,
    totalOperating,
    mortgage: monthlyMortgage,
    total,
  };
}

/**
 * Calculate cash flow metrics
 * @param monthlyRent - Monthly rental income
 * @param expenses - Monthly expense breakdown
 * @param purchasePrice - Property purchase price
 * @param downPayment - Down payment amount
 * @param closingCosts - Closing costs (optional)
 * @returns Cash flow metrics
 */
export function calculateCashFlowMetrics(
  monthlyRent: number,
  expenses: MonthlyExpenseBreakdown,
  purchasePrice: number,
  downPayment: number,
  closingCosts: number = 0,
): CashFlowMetrics {
  const monthlyNOI = monthlyRent - expenses.totalOperating;
  const annualNOI = monthlyNOI * 12;
  const monthlyCashFlow = monthlyRent - expenses.total;
  const annualCashFlow = monthlyCashFlow * 12;

  const capRate = purchasePrice > 0 ? (annualNOI / purchasePrice) * 100 : 0;
  const totalInvestment = downPayment + closingCosts;
  const cashOnCash =
    totalInvestment > 0 ? (annualCashFlow / totalInvestment) * 100 : 0;

  return {
    monthlyNOI,
    annualNOI,
    monthlyCashFlow,
    annualCashFlow,
    capRate,
    cashOnCash,
  };
}

/**
 * Calculate LTV (Loan-to-Value) ratio
 * @param loanAmount - Current loan balance
 * @param propertyValue - Current property value
 * @returns LTV as percentage
 */
export function calculateLTV(
  loanAmount: number,
  propertyValue: number,
): number {
  if (propertyValue <= 0) return 0;
  return (loanAmount / propertyValue) * 100;
}

/**
 * Calculate Portfolio LTV (Loan-to-Value) ratio
 * @param totalDebt - Total loan balance across all properties
 * @param totalValue - Total property value across all properties
 * @returns LTV as percentage
 */
export function calculatePortfolioLTV(
  totalDebt: number,
  totalValue: number,
): number {
  if (totalValue <= 0) return 0;
  return (totalDebt / totalValue) * 100;
}

/**
 * Calculate DSCR (Debt Service Coverage Ratio)
 * @param annualNOI - Annual Net Operating Income
 * @param annualDebtService - Annual debt payments
 * @returns DSCR ratio
 */
export function calculateDSCR(
  annualNOI: number,
  annualDebtService: number,
): number {
  if (annualDebtService <= 0) return 0;
  return annualNOI / annualDebtService;
}

/**
 * Calculate break-even metrics
 * @param monthlyRent - Monthly rental income
 * @param monthlyExpenses - Total monthly expenses
 * @param purchasePrice - Property purchase price
 * @param targetCapRate - Target cap rate (default 7%)
 * @returns Break-even calculations
 */
export function calculateBreakEven(
  monthlyRent: number,
  monthlyExpenses: number,
  purchasePrice: number,
  targetCapRate: number = 7,
): { breakEvenPrice: number; breakEvenRent: number } {
  const annualNetIncome = (monthlyRent - monthlyExpenses) * 12;
  const breakEvenPrice = annualNetIncome / (targetCapRate / 100);
  const breakEvenRent =
    (purchasePrice * (targetCapRate / 100)) / 12 + monthlyExpenses;

  return {
    breakEvenPrice: breakEvenPrice > 0 ? breakEvenPrice : 0,
    breakEvenRent: breakEvenRent > 0 ? breakEvenRent : 0,
  };
}

/**
 * Estimate monthly rent based on property price
 * @param purchasePrice - Property purchase price
 * @param rentToValueRatio - Monthly rent as percentage of value (default 0.6%)
 * @returns Estimated monthly rent
 */
export function estimateMonthlyRent(
  purchasePrice: number,
  rentToValueRatio: number = FINANCIAL_RATES.RENT_TO_PRICE_RATIO * 100,
): number {
  return purchasePrice * (rentToValueRatio / 100);
}

/**
 * Calculate closing costs
 * @param purchasePrice - Property purchase price
 * @param rate - Closing cost rate as percentage (default 3%)
 * @returns Estimated closing costs
 */
export function calculateClosingCosts(
  purchasePrice: number,
  rate: number = FINANCIAL_RATES.CLOSING_COST_RATE * 100,
): number {
  return purchasePrice * (rate / 100);
}
